import React, { ForwardedRef, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { TooltipDefinitions } from './TooltipDefinitions';
import DomUtils from '../../classes/utilities/DomUtils';
import Spacer from '../layout/Spacer';
import { useTypedSelector } from '../../state/hooks';
import { ITooltipDefinition, IVector2 } from '../../interfaces/Misc';
import useEventListener from '../reactUtils/useEventListener';
import useForceUpdate from '../reactUtils/useForceUpdate';

/**
 * monitors the currently hovered element, looking the the 'tooltip' prop on it so the propert tooltip can be displayed
 * @export
 * @returns the content of the tooltipDefinition matching the tooltip prop value on the hovered element, or a default message otherwise
 */
export default function TooltipDisplay({forceTooltip}: {forceTooltip?: boolean})
{
	const currentLanguage = useTypedSelector(state => state.applicationModule.currentLanguage);
	const tooltipsEnabled = useTypedSelector(state => state.preferencesModule.editorPreferences.enableTooltips);

	const tooltipDelay = 0.3 * 1000; // seconds to milliseconds

	const [displayTooltip, setDisplayTooltip] = useState(forceTooltip ?? tooltipsEnabled);
	const [tooltip, setTooltip] = useState<ITooltipDefinition | undefined>(undefined);
	const tooltipKey = useRef<string>("default");
	const displayTimeout = useRef<NodeJS.Timeout>();
	const floaterRef = useRef<HTMLDivElement>(null);
	const floaterPos = useRef({ x: 0, y: 0 });

	const tooltipsRoot = document.getElementsByClassName('helpTooltipRoot')[0] as HTMLElement;

	useEventListener('mousemove', (e: MouseEvent) =>
	{
		floaterPos.current = { x: e.x + 4, y: e.y + 4 };

		// don't close tooltip prompt while mouse is over it
		const isChild = DomUtils.isDomElementDescendantOfRoot(e.target as HTMLElement, tooltipsRoot);
		if (isChild) return;

		const hoveredTooltipKey = DomUtils.getAttributeFromElementRecursive(e.target as HTMLElement, "tooltip") ?? DomUtils.getAttributeFromElementRecursive(e.target as HTMLElement, "data-tooltip");
		if (hoveredTooltipKey !== tooltipKey.current)
		{
			setDisplayTooltip(false);
			tooltipKey.current = hoveredTooltipKey ?? "";
		}

		clearTimeout(displayTimeout.current);
		displayTimeout.current = setTimeout(() =>
		{
			setTooltip(TooltipDefinitions[tooltipKey.current]);
			setDisplayTooltip(forceTooltip ?? tooltipsEnabled);
		}, tooltipDelay);
	}, document);

	useEventListener('mousedown', (e: MouseEvent) =>
	{
		if(!floaterRef.current) return;

		const isChild = DomUtils.isDomElementDescendantOfRoot(e.target as HTMLElement, floaterRef.current);
		if (isChild)
		{
			setTimeout(() =>
			{
				setDisplayTooltip(false);
			}, 150);
			return;
		}

		clearTimeout(displayTimeout.current);
		setDisplayTooltip(false);
	}, document);

	return (
		<div className={"helpTooltipRoot"}>
			{
				!displayTooltip ? null :
					<TooltipFloater ref={floaterRef}
						tooltipContent={tooltip}
						currentLanguage={currentLanguage}
						position={floaterPos.current}
						onClick={() => setDisplayTooltip(false)}
					/>
			}
		</div>
	);
}

interface TooltipFloaterProps 
{
	position?: IVector2;
	tooltipContent?: ITooltipDefinition;
	currentLanguage: string;
	onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const TooltipFloater = React.forwardRef((props: TooltipFloaterProps, forwardedRef: ForwardedRef<HTMLDivElement>) =>
{
	const [pos, setPos] = useState(props.position ?? { x: 0, y: 0 });
	const tooltip = props.tooltipContent;
	const forceUpdate = useForceUpdate();

	const ref = useRef<HTMLDivElement | null>();

	useLayoutEffect(() =>
	{
		if(!ref.current) return;

		const rect = DomUtils.getDomElementRect(ref.current);
		if (rect)
		{
			if (rect.left + rect.width > window.innerWidth)
			{
				setPos(prevPos => ({ ...prevPos, x: window.innerWidth - rect.width }));
			}

			if (rect.top + rect.height > window.innerHeight)
			{
				setPos(prevPos => ({ ...prevPos, y: window.innerHeight - rect.height }));
			}

			forceUpdate();
		}
	}, [props.position, ref]);

	useEffect(() =>
	{
		if(!ref.current) return;

		const rect = ref.current.getBoundingClientRect();
		if(rect.height >= rect.width)
		{
			pos.x -= rect.width / 2;
		}
	}, [ref]);

	return !tooltip ? null : (
		<div ref={(node) => 
		{
			ref.current = node;
			if(typeof forwardedRef === "function") forwardedRef(node);
			else if(forwardedRef) forwardedRef.current = node;
		}}
		className="tooltipFloaterRoot"
		style={{
			left: pos.x + "px",
			top: pos.y + "px",
		}}
		onClick={props.onClick}
		>
			<div className={"tooltipContentRoot"}>
				{
					tooltip.content[props.currentLanguage]
				}
				{
					!(tooltip.keybinding && tooltip.keybinding()) ? null :
						<>
							<Spacer size={{ y: "8px" }} />
							<div className={"tooltipShortcut"}>
								Shortcut: <span className={"tooltipKeybinding"}>{tooltip.keybinding()}</span>
							</div>
						</>
				}
			</div>
		</div>
	);
});